import { comparer, reaction } from 'mobx';

function routeKey(location) {
    return `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
}

export function installDialogLifecycle({ store, history, getContext }) {
    let currentPath = routeKey(history.location);
    const stopHistory = history.listen(location => {
        const nextPath = routeKey(location);
        if (nextPath !== currentPath) store.cancelAll();
        currentPath = nextPath;
    });
    const stopContext = reaction(getContext, (context, previous) => {
        if (previous) store.cancelAll();
        store.setBlocked(!!context.locked);
    }, { equals: comparer.structural, fireImmediately: true });

    return () => {
        stopHistory();
        stopContext();
        store.cancelAll();
    };
}

// Subscribe through Cordova's current document API. The bridge can arrive after the host mounts.
// This watcher exists only while a dialog is visible, so idle Back behavior remains Cordova's default.
export function watchDialogBackButton({ store, win = window, doc = document }) {
    let stopped = false;
    let timer;
    let removeReady;
    let removeBack;
    const onBack = event => {
        const request = store.current;
        if (!request) return;
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
        store.dismiss(request.id);
    };
    const onReady = () => {
        if (stopped || removeBack || !store.requests.length) return;
        doc.addEventListener('backbutton', onBack, false);
        removeBack = () => doc.removeEventListener('backbutton', onBack, false);
    };
    const connect = () => {
        if (stopped) return;
        const platform = win.cordova?.platformId || new URLSearchParams(win.location?.search).get('platform');
        if (platform !== 'android') return;
        if (!win.cordova) {
            timer = win.setTimeout(connect, 250);
            return;
        }
        doc.addEventListener('deviceready', onReady, false);
        removeReady = () => doc.removeEventListener('deviceready', onReady, false);
    };
    connect();
    return () => {
        stopped = true;
        win.clearTimeout(timer);
        if (removeReady) removeReady();
        if (removeBack) removeBack();
    };
}
